import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, concat, map, of, switchMap, timer } from 'rxjs';

import { AppState } from '@state/app.state';
import { dismissMaintenanceNotice } from '@state/system/system.actions';
import { selectVisibleMaintenanceNotice } from '@state/system/system.selectors';

interface MaintenanceBannerViewModel {
  start: string;
  end: string;
}

@Component({
  selector: 'app-maintenance-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './maintenance-banner.component.html',
  styleUrl: './maintenance-banner.component.scss'
})
export class MaintenanceBannerComponent implements OnInit {
  notice$!: Observable<MaintenanceBannerViewModel | null>;

  private readonly DATE_FORMAT = 'dd/MM/yyyy HH:mm';

  constructor(private store: Store<AppState>, private datePipe: DatePipe) {}

  ngOnInit(): void {
    this.notice$ = this.store.select(selectVisibleMaintenanceNotice).pipe(
      switchMap(notice => {
        if (!notice?.end_at) return of(null);

        const msUntilEnd = new Date(notice.end_at).getTime() - Date.now();

        if (msUntilEnd <= 0) return of(null);

        // Hide the banner the moment the window ends instead of waiting for the next poll.
        return concat(of(notice), timer(msUntilEnd).pipe(map(() => null)));
      }),
      map(notice =>
        notice
          ? {
              start: this.datePipe.transform(notice.start_at, this.DATE_FORMAT) ?? '',
              end: this.datePipe.transform(notice.end_at, this.DATE_FORMAT) ?? ''
            }
          : null
      )
    );
  }

  onClose(): void {
    this.store.dispatch(dismissMaintenanceNotice());
  }
}
